import React, { useEffect, useState, Suspense, lazy } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  Grid,
  CircularProgress,
} from '@mui/material';
import useGeofences from 'src/hooks/useGeofences';
import useEventBus from 'src/hooks/useEventBus';
import { useAddSelectedGeofence } from 'src/hooks/teamGeofences';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import LazyLoadScript from 'src/components/LazyLoadScript';

const GoogleMapGeofences = lazy(
  () => import('src/components/GoogleMaps/GoogleMapGeofences')
);

const GeozonesSelectorDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: addSelectedGeofence, isLoading: loadingAddGeofences } =
    useAddSelectedGeofence();
  const { geofences, activeGeofences, refetchActiveGeofences } = useGeofences();
  const emitSwitchStart = useEventBus('geozones', 'switchStart');
  const emitSwitchEnd = useEventBus('geozones', 'switchEnd');

  const [value, setValue] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const handleAddGeofence = async () => {
    try {
      onClose();
      emitSwitchStart();
      await addSelectedGeofence({
        geofencesIds: value.map((item) => item.id),
      });
      await refetchActiveGeofences();
      emitSwitchEnd();
      enqueueSnackbar(t('geozone.selector.updated'), {
        variant: 'success',
      });
    } catch (err) {
      emitSwitchEnd();
      enqueueSnackbar(err.message, {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    setValue(activeGeofences);
  }, [activeGeofences]);
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('geozone.selector.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('geozone.selector.description')}
        </DialogContentText>

        <Autocomplete
          sx={{ mb: 2, mt: 3 }}
          multiple
          id="search-geozone-here"
          options={geofences}
          getOptionLabel={(option) => option.name}
          filterSelectedOptions
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('sidebar.geozones')}
              placeholder={t('geozone.selector.search-placeholder')}
            />
          )}
          value={value}
          inputValue={inputValue}
          onChange={(_, newValue) => {
            setValue(newValue);
          }}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue);
          }}
          clearText={t('geozone.selector.clear-active')}
        />
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
        >
          <Grid>
            <Button
              onClick={() => {
                setValue([]);
              }}
            >
              {t('geozone.selector.clear-all')}
            </Button>
          </Grid>
          <Grid>
            <Button
              onClick={() => {
                if (value.length !== geofences.length) {
                  setValue(geofences);
                }
              }}
            >
              {t('common.select-all')}
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ flexDirection: 'column' }}></Box>

        <Box sx={{ mt: 3 }}>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '40vh',
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <LazyLoadScript>
              <GoogleMapGeofences
                sx={{
                  width: '100%',
                  height: '40vh',
                  borderRadius: '10px',
                }}
                geofences={value}
              />
            </LazyLoadScript>
          </Suspense>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loadingAddGeofences}
        >
          {t('common.close')}
        </Button>
        <Button
          variant="contained"
          onClick={handleAddGeofence}
          disabled={loadingAddGeofences}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function useGeozonesSelectorDialog() {
  const [geozonesOpen, setGeozonesOpen] = useState(false);
  const handleChangeGeozones = () => {
    setGeozonesOpen(true);
  };

  useEventBus('geozones', 'openSelector', handleChangeGeozones, [
    setGeozonesOpen,
  ]);

  return {
    handleChangeGeozones,
    GeozonesSelectorDialog: () => (
      <GeozonesSelectorDialog
        open={geozonesOpen}
        onClose={() => setGeozonesOpen(false)}
      />
    ),
  };
}

export default useGeozonesSelectorDialog;
